import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import apiClient from '../utils/apiClient';

const SimpleInvitations = () => {
  const { t } = useTranslation();
  
  const roleOptions = [
    { value: 'user', label: t('simpleInvitations.user') },
    { value: 'org-admin', label: t('simpleInvitations.orgAdmin') }
  ];
  const [invites, setInvites] = useState([]);
  const [loading, setLoading] = useState(false);
  const [creating, setCreating] = useState(false);
  const [form, setForm] = useState({ role: 'user', expiresInMinutes: 30, maxUses: 1, note: '' });
  const [newCode, setNewCode] = useState(null);
  const [error, setError] = useState(null);
  const [copied, setCopied] = useState(false);

  const loadInvites = async () => {
    setLoading(true);
    try {
      const res = await apiClient.get('/api/admin/invitations');
      setInvites(res.data || []);
    } catch (e) {
      setError(e.response?.data?.error || e.message);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { loadInvites(); }, []);

  const createInvite = async (e) => {
    e.preventDefault();
    setCreating(true);
    setError(null);
    setNewCode(null);
    try {
      const res = await apiClient.post('/api/admin/invitations', form);
      setNewCode(res.data.code);
      await loadInvites();
    } catch (e) {
      setError(e.response?.data?.error || e.message);
    } finally {
      setCreating(false);
    }
  };

  const revoke = async (id) => {
    if (!window.confirm(t('simpleInvitations.revokeConfirmation'))) return;
    try {
      await apiClient.patch(`/api/admin/invitations/${id}/revoke`);
      await loadInvites();
    } catch (e) {
      setError(e.response?.data?.error || e.message);
    }
  };

  const copyToClipboard = async (code) => {
    try {
      await navigator.clipboard.writeText(code);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000); // Reset after 2 seconds
    } catch (err) {
      // Fallback pour navigateurs sans support clipboard API
      const textArea = document.createElement('textarea');
      textArea.value = code;
      document.body.appendChild(textArea);
      textArea.select();
      document.execCommand('copy');
      document.body.removeChild(textArea);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    }
  };

  const statusBadge = (status) => {
    const colors = { active: '#16a34a', expired: '#dc2626', revoked: '#9333ea', consumed: '#0ea5e9' };
    return <span style={{ color: colors[status] || '#6b7280', fontWeight: 'bold' }}>{status}</span>;
  };

  const th = { textAlign: 'left', padding: '6px 8px', background: '#1e293b', color: '#fff', fontSize: 12 };
  const td = { padding: '6px 8px', fontSize: 13 };

  return (
    <div style={{ padding: 16 }}>
      <h3>🎫 {t('simpleInvitations.invitationCodes')}</h3>
      <form onSubmit={createInvite} style={{ display: 'flex', gap: 12, marginBottom: 16, flexWrap: 'wrap' }}>
        <div style={{ flex: 1, minWidth: 120 }}>
          <label>{t('simpleInvitations.role')}<br />
            <select value={form.role} onChange={e=> setForm(f=>({...f, role: e.target.value}))}>
              {roleOptions.map(opt => <option key={opt.value} value={opt.value}>{opt.label}</option>)}
            </select>
          </label>
        </div>
        <div style={{ flex: 1, minWidth: 120 }}>
          <label>{t('simpleInvitations.expiresInMinutes')}<br />
            <input type="number" min={1} max={10080} value={form.expiresInMinutes} onChange={e=> setForm(f=>({...f, expiresInMinutes: parseInt(e.target.value,10)}))} />
          </label>
        </div>
        <div style={{ flex: 1, minWidth: 120 }}>
          <label>{t('simpleInvitations.maxUses')}<br />
            <input type="number" min={1} max={20} value={form.maxUses} onChange={e=> setForm(f=>({...f, maxUses: parseInt(e.target.value,10)}))} />
          </label>
        </div>
        <div style={{ flex: 1, minWidth: 200 }}>
          <label>{t('simpleInvitations.optionalNote')}<br />
            <input type="text" value={form.note} onChange={e=> setForm(f=>({...f, note: e.target.value}))} />
          </label>
        </div>
        <button disabled={creating} className="btn-primary">{creating ? t('simpleInvitations.creating') : t('simpleInvitations.create')}</button>
      </form>
      {error && <div style={{ color: 'red', marginBottom: 12 }}>{error}</div>}
      {newCode && (
        <div style={{ 
          background: 'linear-gradient(135deg, #0f172a 0%, #1e293b 100%)', 
          color: '#fff', 
          padding: '16px 20px', 
          borderRadius: 12, 
          marginBottom: 16,
          border: '1px solid #334155',
          position: 'relative'
        }}>
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 8 }}>
            <strong style={{ color: '#10b981' }}>✅ {t('simpleInvitations.newCodeCreated')}</strong>
            <button
              onClick={() => copyToClipboard(newCode)}
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: 8,
                padding: '8px 12px',
                background: copied ? '#10b981' : '#4f46e5',
                color: 'white',
                border: 'none',
                borderRadius: 8,
                cursor: 'pointer',
                fontSize: 14,
                fontWeight: 600,
                transition: 'all 0.2s ease',
                transform: copied ? 'scale(1.05)' : 'scale(1)'
              }}
              title={copied ? t('simpleInvitations.codeCopied') : t('simpleInvitations.copyCode')}
            >
              {copied ? (
                <>
                  ✅ {t('simpleInvitations.copied')}
                </>
              ) : (
                <>
                  📋 {t('simpleInvitations.copy')}
                </>
              )}
            </button>
          </div>
          <div style={{ 
            fontFamily: 'Monaco, "Cascadia Code", "Roboto Mono", monospace', 
            fontSize: 20, 
            letterSpacing: 2,
            padding: '12px 16px',
            background: 'rgba(0,0,0,0.3)',
            borderRadius: 8,
            border: '1px solid #475569',
            color: '#fbbf24'
          }}>
            {newCode}
          </div>
          <div style={{ fontSize: 12, color: '#94a3b8', marginTop: 8 }}>
            💡 {t('simpleInvitations.codeNotDisplayedAgain')}
          </div>
        </div>
      )}
      <div style={{ overflowX: 'auto' }}>
        <table style={{ width: '100%', borderCollapse: 'collapse' }}>
          <thead>
            <tr>
              <th style={th}>{t('simpleInvitations.fingerprint')}</th>
              <th style={th}>{t('simpleInvitations.role')}</th>
              <th style={th}>{t('simpleInvitations.created')}</th>
              <th style={th}>{t('simpleInvitations.expires')}</th>
              <th style={th}>{t('simpleInvitations.usage')}</th>
              <th style={th}>{t('simpleInvitations.status')}</th>
              <th style={th}>{t('simpleInvitations.actions')}</th>
            </tr>
          </thead>
          <tbody>
            {loading && <tr><td colSpan={7}>{t('simpleInvitations.loading')}</td></tr>}
            {!loading && invites.length === 0 && <tr><td colSpan={7}>{t('simpleInvitations.noCodes')}</td></tr>}
            {invites.map(inv => (
              <tr key={inv.id} style={{ borderTop: '1px solid #334155' }}>
                <td style={td}>{inv.fingerprint}</td>
                <td style={td}>{inv.roleGranted}</td>
                <td style={td}>{new Date(inv.createdAt).toLocaleString()}</td>
                <td style={td}>{new Date(inv.expiresAt).toLocaleTimeString()}</td>
                <td style={td}>{inv.usedCount}/{inv.maxUses}</td>
                <td style={td}>{statusBadge(inv.status)}</td>
                <td style={td}>
                  {inv.status === 'active' && <button onClick={()=> revoke(inv.id)} style={{ padding: '2px 6px' }}>{t('simpleInvitations.revoke')}</button>}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
};

export default SimpleInvitations;
